import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { notify } from '@/lib/notify';

export type NotificationLevel = 'info' | 'success' | 'warning' | 'error';

export interface AppNotification {
  id: string;
  level: NotificationLevel;
  title: string;
  message?: string;
  createdAt: string;
  read: boolean;
  /** Optional link the notification navigates to when clicked. */
  href?: string;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  /** Add an in-app notification; `toast` also fires a transient toast. */
  push: (n: Omit<AppNotification, 'id' | 'createdAt' | 'read'> & { toast?: boolean }) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  clear: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);
const MAX = 50;

/**
 * The in-app Notification System: a persistent (session-scoped) center of app
 * notifications, distinct from transient toasts. Any feature can `push` an
 * event; the bell/panel renders the list with unread state. Optionally mirrors
 * to a toast for immediate visibility.
 */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  const push = useCallback<NotificationContextValue['push']>((n) => {
    const item: AppNotification = {
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      read: false,
      level: n.level,
      title: n.title,
      message: n.message,
      href: n.href,
    };
    setNotifications((prev) => [item, ...prev].slice(0, MAX));
    if (n.toast) notify[n.level](n.message ? `${n.title} — ${n.message}` : n.title);
  }, []);

  const markRead = useCallback((id: string) => setNotifications((p) => p.map((x) => (x.id === id ? { ...x, read: true } : x))), []);
  const markAllRead = useCallback(() => setNotifications((p) => p.map((x) => ({ ...x, read: true }))), []);
  const remove = useCallback((id: string) => setNotifications((p) => p.filter((x) => x.id !== id)), []);
  const clear = useCallback(() => setNotifications([]), []);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const value = useMemo(
    () => ({ notifications, unreadCount, push, markRead, markAllRead, remove, clear }),
    [notifications, unreadCount, push, markRead, markAllRead, remove, clear],
  );
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
